import { useTranslation } from "@/hooks";
import {
	PaymentAddDialogStatus,
	PaymentAddDialogUsdtInfo,
} from "@/pages/payment/shared";
import { useActOnPayment } from "@/services";
import {
	ActionItem,
	ActionMenu,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Typography,
	toast,
} from "@st-marketplace/ui";
import { useState } from "react";

import type { PaymentRow } from "../../../../../types";

interface PaymentReceivedTableActionProps {
	payment: PaymentRow;
}

const MANUAL_PAYMENT_TYPES = ["MARKET_MANUAL", "SYSTEM_MANUAL"];

function PaymentReceivedTableAction({
	payment,
}: PaymentReceivedTableActionProps) {
	const { t } = useTranslation();

	const canAcceptOrRejectPayment =
		payment.state === "pending" &&
		payment.type &&
		MANUAL_PAYMENT_TYPES.includes(payment.type);

	const [menuActionOpen, setMenuActionOpen] = useState<boolean>(false);
	const [rejectDialog, setRejectDialog] = useState<boolean>(false);
	const [acceptDialog, setAcceptDialog] = useState<boolean>(false);

	const actOnPayment = useActOnPayment();
	const handlePaymentAction = (action: "approve" | "reject") => {
		actOnPayment.mutate(
			{
				path: { payment_id: payment.id },
				body: {
					type_:
						payment.type === "MARKET_MANUAL"
							? "MARKET_PAYMENT_ACTION"
							: "SYSTEM_PAYMENT_ACTION",
					action: action,
				},
			},
			{
				onSuccess: () => {
					toast.success(
						action === "approve"
							? t("payment.action.acceptSuccess")
							: t("payment.action.rejectSuccess"),
					);
					setAcceptDialog(false);
					setRejectDialog(false);
					setMenuActionOpen(false);
				},
			},
		);
	};

	const [paymentInfoStatusDialog, setPaymentInfoStatusDialog] =
		useState<boolean>(false);

	const [isUsdtDialogOpen, setIsUsdtDialogOpen] = useState<boolean>(false);
	const [isConfirmedDialogOpen, setIsConfirmedDialogOpen] =
		useState<boolean>(false);

	const handlePaymentInfoOpen = () => {
		if (
			(payment.type === "MARKET_AUTOMATIC" ||
				payment.type === "SYSTEM_AUTOMATIC") &&
			payment.state === "pending"
		) {
			setIsUsdtDialogOpen(true);
		} else {
			setPaymentInfoStatusDialog(true);
		}
	};

	return (
		<>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-only container; inner menu remains keyboard-accessible */}
			<div onClick={(event) => event.stopPropagation()}>
				<ActionMenu open={menuActionOpen} onOpenChange={setMenuActionOpen}>
					{canAcceptOrRejectPayment ? (
						<>
							<ActionItem
								leadingIconName="check-line"
								loading={actOnPayment.isPending}
								onClick={() => {
									setMenuActionOpen(false);
									setAcceptDialog(true);
								}}
							>
								{t("action.accept")}
							</ActionItem>
							<ActionItem
								leadingIconName="close-line"
								loading={actOnPayment.isPending}
								onClick={() => {
									setMenuActionOpen(false);
									setRejectDialog(true);
								}}
							>
								{t("action.reject")}
							</ActionItem>
						</>
					) : (
						<ActionItem
							leadingIconName="information-line"
							onClick={() => {
								setMenuActionOpen(false);
								handlePaymentInfoOpen();
							}}
						>
							{t("action.details")}
						</ActionItem>
					)}
				</ActionMenu>
			</div>

			<Dialog open={acceptDialog} onOpenChange={setAcceptDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
					</DialogHeader>
					<div className="px-4">
						<Typography variant="body-md">
							{t("payment.action.acceptMessage")}
						</Typography>
					</div>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.no"),
								props: {
									type: "button",
									onClick: () => setAcceptDialog(false),
								},
							},
							primary: {
								label: t("action.button.confirmAccept"),
								props: {
									type: "button",
									loading: actOnPayment.isPending,
									onClick: () => handlePaymentAction("approve"),
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>

			<Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
					</DialogHeader>
					<div className="px-4">
						<Typography variant="body-md">
							{t("payment.action.rejectMessage")}
						</Typography>
					</div>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.no"),
								props: {
									type: "button",
									onClick: () => setRejectDialog(false),
								},
							},
							primary: {
								label: t("action.button.confirmReject"),
								props: {
									type: "button",
									color: "danger",
									loading: actOnPayment.isPending,
									onClick: () => handlePaymentAction("reject"),
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>

			<PaymentAddDialogStatus
				method={payment.state}
				open={paymentInfoStatusDialog}
				onOpenChange={setPaymentInfoStatusDialog}
				// message={t("payment.received.statusDialog.infoMessage", {})}
			/>

			{payment.type === "MARKET_AUTOMATIC" ||
			payment.type === "SYSTEM_AUTOMATIC" ? (
				<>
					<PaymentAddDialogUsdtInfo
						data={payment}
						open={isUsdtDialogOpen}
						onOpenChange={setIsUsdtDialogOpen}
						onSuccess={() => {
							setIsUsdtDialogOpen(false);
							setIsConfirmedDialogOpen(true);
						}}
					/>
					<PaymentAddDialogStatus
						method="accepted"
						open={isConfirmedDialogOpen}
						onOpenChange={setIsConfirmedDialogOpen}
					/>
				</>
			) : null}
		</>
	);
}

export default PaymentReceivedTableAction;
